import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationService } from 'src/app/services/application-service';
import { handleApiError } from 'src/app/utils/error';
import { Subscription, timer } from 'rxjs';
import { FlowService } from 'src/app/services/flow.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { CompanyApiService } from 'src/app/services/api/company-api.service';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import { ActivatedRoute } from '@angular/router';
import { ibanValidator } from 'src/app/validators/ibanValidator';
import { debounceTime } from 'rxjs/operators';

@Component({
  selector: 'app-iban',
  templateUrl: './iban.component.html',
  styleUrls: ['./iban.component.scss'],
})
export class IbanComponent implements OnInit, OnDestroy {
  applicationId!: number;
  ibanForm!: FormGroup;
  error!: string;
  subs: Array<Subscription> = [];
  requestPending = false;
  isMinaSidor = false;
  isLoading = false;
  @Input() isSummary!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private applicationService: ApplicationService,
    private companyApi: CompanyApiService,
    public flowService: FlowService,
    protected trackingService: TrackingService,
    private route: ActivatedRoute,
    private applicationApi: ApplicationsApiService,
  ) {
  }

  ngOnInit(): void {
    this.applicationId = this.applicationService.applicationId;

    this.subs.push(
      this.route.queryParams.subscribe((params) => {
        const appId = this.applicationId;
        this.isMinaSidor = params.token;
        if (!this.isMinaSidor && appId) {
          this.isLoading = true;
          this.applicationApi.getApplication(appId).subscribe((response) => {
            this.applicationService.iban = response.company.iban;
            this.iban.patchValue(this.applicationService.iban);
          },
          (error: any) => handleApiError(error, this.translateService),
          () => this.isLoading = false
          )
        }
      })
    );

    const initialIban = this.applicationService.iban;

    this.ibanForm = this.formBuilder.group({
      iban: [initialIban, [Validators.pattern('[A-Za-z0-9 ]*'), ibanValidator()]],
    });

    this.subs.push(
      this.iban.valueChanges
        .pipe(debounceTime(1000))
        .subscribe((v) => {
          if (v && this.form.valid) {
            this.requestPending = true;
            const ibanNumber = this.cleanUpString(v);
            this.companyApi.updateCompany({
              id: this.applicationService.companyId,
              iban: ibanNumber
            }).subscribe(() => {
              this.applicationService.iban = ibanNumber;
              this.error = '';
              this.trackFields('iban');
            },
              (error:any) => handleApiError(error, this.translateService),
              () => this.requestPending = false
            )
          }
        })
    );
  }

  // Remove all whitespace and transform to uppercase
  cleanUpString(value: string): string {
    if (value) return value.replace(/\s/g,'').toUpperCase();
    return value;
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  handleBackButtonClick(): void {
    this.subs.push(timer(1000).subscribe(() => {
      if (!this.requestPending) {
        this.flowService.previousScreen();
      }
    }));
  }

  handleNextButtonClick(): void {
    this.subs.push(timer(1000).subscribe(() => {
      if (!this.form.invalid) {
        this.flowService.nextScreen();
      }
    }));
  }

  get form(): FormGroup {
    return this.ibanForm;
  }

  get iban(): FormControl {
    return this.form.get('iban') as FormControl;
  }

  getErrors(control: string): string | null {
    const iban = this.iban;

    switch (control) {
      case 'iban':
        if (iban.hasError('invalid') || iban.hasError('pattern')) {
          return this.translateService.instant('global.error.iban.invalid');
        } else if (iban.hasError('countryCodeInvalid')) {
          return this.translateService.instant('global.error.iban.countryCodeInvalid');
        }
        return null;
      default:
        return null;
    }
  }
}
